import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import equipmentService from '../services/equipment';
import type { EquipmentHealthSummary, EquipmentHealthFlaggedItem } from '../types/equipment';
import {
  Tractor, AlertTriangle, Clock, CheckCircle, Wrench,
  ChevronRight, Zap, CreditCard, ShieldAlert,
} from 'lucide-react';
import { cn } from '../utils/cn';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function flagLabel(item: EquipmentHealthFlaggedItem): string {
  switch (item.reason) {
    case 'under_repair':    return 'Under repair';
    case 'maintenance_due': return item.maintenance_due_count > 1
      ? `${item.maintenance_due_count} services due`
      : 'Service due';
    case 'overdue_service': return item.overdue_days ? `Overdue ${item.overdue_days}d` : 'Service overdue';
    case 'due_this_week':   return 'Due this week';
    case 'rego_expired':    return 'Rego expired';
    case 'rego_expiring': {
      const d = item.rego_days_left;
      if (d == null) return 'Rego due';
      return d <= 0 ? 'Rego due today' : d === 1 ? 'Rego tomorrow' : `Rego in ${d}d`;
    }
    case 'insurance_expired':  return 'Insurance expired';
    case 'insurance_expiring': {
      const d = item.insurance_days_left;
      if (d == null) return 'Insurance due';
      return d <= 0 ? 'Insurance due today' : d === 1 ? 'Insurance tomorrow' : `Insurance in ${d}d`;
    }
    case 'insurance_none':     return 'No insurance';
    default:                return item.reason;
  }
}

function flagColour(reason: EquipmentHealthFlaggedItem['reason']): string {
  switch (reason) {
    case 'under_repair':    return 'bg-red-100 text-red-700';
    case 'maintenance_due': return 'bg-amber-100 text-amber-700';
    case 'overdue_service': return 'bg-orange-100 text-orange-700';
    case 'due_this_week':   return 'bg-yellow-100 text-yellow-700';
    case 'rego_expired':    return 'bg-red-100 text-red-700';
    case 'rego_expiring':   return 'bg-amber-100 text-amber-700';
    case 'insurance_expired':  return 'bg-red-100 text-red-700';
    case 'insurance_expiring': return 'bg-amber-100 text-amber-700';
    case 'insurance_none':     return 'bg-neutral-100 text-neutral-700';
    default:                return 'bg-neutral-100 text-neutral-600';
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Dashboard card showing equipment health + quick access.
 * Shown to all authenticated users — no module license required.
 * Auto-hides when the org has no active equipment.
 */
export function EquipmentQuickCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData]     = useState<EquipmentHealthSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    equipmentService.getHealthSummary()
      .then(res => { if (res.success && res.data) setData(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.organisation_id]);

  // Hide until loaded
  if (loading) return null;

  const isEmpty = !data || data.total_active === 0;

  // A server older than the 7-day rego warning sends only the 30-day count;
  // leave rego out rather than show a month's worth as "due".
  const regoExpiring = data?.rego_expiring_7d ?? 0;
  const regoExpired  = data?.rego_expiring_7d != null ? data.rego_expired : 0;
  const insExpired   = data?.insurance_expired ?? 0;
  const insExpiring  = data?.insurance_expiring ?? 0;
  const insNone      = data?.insurance_none ?? 0;

  const allClear = !isEmpty && data !== null &&
    data.under_repair === 0 && data.overdue_service === 0 &&
    data.due_this_week === 0 && data.maintenance_due === 0 &&
    regoExpired === 0 && regoExpiring === 0 &&
    insExpired === 0 && insExpiring === 0 && insNone === 0;

  const flagged = (data?.flagged ?? []).filter(f =>
    data?.rego_expiring_7d != null || (f.reason !== 'rego_expired' && f.reason !== 'rego_expiring'));
  const flaggedToShow = flagged.slice(0, 3);
  const hasFlagged    = !allClear && flaggedToShow.length > 0;

  const totalAlerts = data
    ? data.under_repair + data.maintenance_due + data.overdue_service + data.due_this_week
      + regoExpired + regoExpiring + insExpired + insExpiring + insNone
    : 0;

  return (
    <Card
      className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
      onClick={() => navigate('/equipment')}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg text-neutral-600">Equipment</CardTitle>
        <div className={cn(
          'h-12 w-12 rounded-xl flex items-center justify-center',
          totalAlerts > 0 ? 'bg-amber-100' : 'bg-green-100'
        )}>
          <Tractor className={cn('h-6 w-6', totalAlerts > 0 ? 'text-amber-600' : 'text-green-600')} />
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {isEmpty ? (
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-neutral-100 text-neutral-500 text-sm">
              <Tractor className="h-4 w-4" />
              Add your first vehicle to get started
            </div>
          </div>
        ) : allClear ? (
          /* ── All clear ── */
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
              <CheckCircle className="h-4 w-4" />
              All equipment OK
            </div>
          </div>
        ) : (
          /* ── Attention needed ── */
          <>
            <div className="flex flex-wrap gap-2 mt-1">
              {regoExpired > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                  <CreditCard className="h-3.5 w-3.5" />
                  {regoExpired} Rego Expired
                </div>
              )}
              {regoExpiring > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                  <CreditCard className="h-3.5 w-3.5" />
                  {regoExpiring} Rego Due
                </div>
              )}
              {insExpired > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {insExpired} Insurance Expired
                </div>
              )}
              {insExpiring > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {insExpiring} Insurance Due
                </div>
              )}
              {data!.under_repair > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                  <Wrench className="h-3.5 w-3.5" />
                  {data!.under_repair} Under Repair
                </div>
              )}
              {data!.maintenance_due > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {data!.maintenance_due} Service{data!.maintenance_due > 1 ? 's' : ''} Due
                </div>
              )}
              {data!.overdue_service > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {data!.overdue_service} Overdue
                </div>
              )}
              {data!.due_this_week > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
                  <Clock className="h-3.5 w-3.5" />
                  {data!.due_this_week} Due This Week
                </div>
              )}
              {insNone > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700 text-xs font-semibold">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {insNone} No Insurance
                </div>
              )}
            </div>

            {hasFlagged && (
              <ul className="mt-3 space-y-1.5">
                {flaggedToShow.map(item => (
                  <li key={item.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {item.status === 'active' ? (
                        <Zap className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                      ) : (
                        <Wrench className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                      )}
                      <span className="truncate text-neutral-700 font-medium">{item.name}</span>
                    </div>
                    <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ml-2', flagColour(item.reason))}>
                      {flagLabel(item)}
                    </span>
                  </li>
                ))}
                {(flagged.length > 3) && (
                  <li className="text-xs text-neutral-400 pt-0.5">
                    +{flagged.length - 3} more…
                  </li>
                )}
              </ul>
            )}
          </>
        )}

        <div className="flex items-center justify-end mt-3 text-xs text-green-600 font-medium">
          View all equipment
          <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
        </div>
      </CardContent>
    </Card>
  );
}

export default EquipmentQuickCard;
